// 파티 던전 클라 연결(docs/PARTY-TRADE-PRD.md 3장, 레인 D). 게임·렌더와 분리된 네트워크 층.
// - 연결: 같은 출처 /api/party/ws?code=new|<코드> (wss). 끊기면 지수 백오프(0.5 → 8초)로 같은 코드에 다시 붙는다(서버가 자리를 60초 유지).
// - 로비: room(명단·준비·단계), 보내기 ready/tier/start/leave/kick.
// - 판: go → spawn/wave/s(스냅)/atk/hurt/die → end. 몹·동료는 스냅 두 개 사이를 INTERP_MS 뒤 시각으로 보간한다(FieldNet과 같은 performance.now 시계).
// - 게임 연결(src/game/partyLink.ts)은 매 틱 update(hero)로 위치를 보내고, drain()으로 사건(spawn·atk·hurt·die·end)을 받는다.
// 오프라인·서버 오류면 예외를 던지지 않는다(error 문자열만).
import {
  PARTY_KEEPALIVE_MS, PARTY_PROTO_VERSION, PARTY_TICK_MS, PARTY_WS_PATH,
  type PartyClientMsg, type PartyRoomView, type PartyServerMsg,
} from '../shared/partyNet';
import type { HeroId } from '../shared/content/types';

export interface PartySocketLike {
  readyState: number;
  onopen: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onclose: ((ev: { code: number; reason?: string }) => void) | null;
  onerror: ((ev: unknown) => void) | null;
  send(data: string): void;
  close(code?: number, reason?: string): void;
}
export type PartyStatus = 'idle' | 'connecting' | 'online' | 'retrying' | 'closed';

export interface PartyClientOptions {
  name: string;
  hero: HeroId;
  lv: number;
  /** 접속 주소 직접 지정(QA 서버, ws://…). 없으면 같은 출처 */
  url?: string;
  now?: () => number;
  socket?: (url: string) => PartySocketLike;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (id: unknown) => void;
}

/** 보간 대상 하나(몹·동료) */
export interface PartyTrack {
  id: number;
  /** 몹: 종류·정예·체력·상태 / 동료: 칸·동작 */
  kind: number; elite: boolean; hp: number; max: number; st: number;
  ax: number; az: number; af: number; at: number;
  bx: number; bz: number; bf: number; bt: number;
  /** 보간한 지금 위치(sample()이 채운다) */
  x: number; z: number; f: number;
  seen: boolean;
}
export type PartyEvent =
  | { t: 'go'; runId: string; tier: number; at: number }
  | { t: 'spawn'; id: number; kind: number; elite: boolean; x: number; z: number; hp: number; max: number }
  | { t: 'wave'; n: number; of: number }
  | { t: 'atk'; id: number; x: number; z: number; r: number; ms: number }
  | { t: 'hurt'; id: number; slots: number[]; d: number }
  | { t: 'die'; id: number; by: number }
  | { t: 'correct'; x: number; z: number }
  | Extract<PartyServerMsg, { t: 'end' }>;

/** 보간 지연(스냅 10Hz 한 칸) */
export const INTERP_MS = PARTY_TICK_MS;
const MAX_EVENTS = 256;

function track(id: number): PartyTrack {
  return { id, kind: 0, elite: false, hp: 0, max: 1, st: 0, ax: 0, az: 0, af: 0, at: 0, bx: 0, bz: 0, bf: 0, bt: 0, x: 0, z: 0, f: 0, seen: false };
}
function lerpAngle(a: number, b: number, k: number): number {
  // 반복문 대신 나머지로 접는다(서버가 보낸 값이 아주 커도 멈추지 않게 — PR #243 Codex P1)
  if (!Number.isFinite(a) || !Number.isFinite(b)) return Number.isFinite(b) ? b : 0;
  const tau = Math.PI * 2;
  let d = (b - a) % tau;
  if (d > Math.PI) d -= tau; else if (d < -Math.PI) d += tau;
  return a + d * k;
}

export class PartyClient {
  status: PartyStatus = 'idle';
  room: PartyRoomView | null = null;
  you = -1;
  error = '';
  /** 판 진행 중인지(go ~ end) */
  inRun = false;
  runId = '';
  tier = 1;
  startAt = 0;
  readonly mobs = new Map<number, PartyTrack>();
  readonly peers = new Map<number, PartyTrack>();
  /** 마지막 판 결과(end) */
  lastEnd: Extract<PartyServerMsg, { t: 'end' }> | null = null;
  /** 바뀔 때마다 오른다(UI가 다시 그릴지 판단) */
  revision = 0;

  private ws: PartySocketLike | null = null;
  private code = '';
  private wantOpen = false;
  private backoff = 500;
  private joinFails = 0;
  /** 마지막으로 보낸 hello(영웅 전환 동기화용) */
  private helloSent: { name: string; hero: string; lv: number } | null = null;
  private retryTimer: unknown = null;
  private pingTimer: unknown = null;
  private lastSend = 0;
  private sentX = NaN; private sentZ = NaN; private sentF = NaN; private sentA = -1;
  private readonly events: PartyEvent[] = [];
  private readonly now: () => number;
  private readonly setTimer: (fn: () => void, ms: number) => unknown;
  private readonly clearTimer: (id: unknown) => void;

  constructor(private readonly o: PartyClientOptions) {
    this.now = o.now ?? (() => (typeof performance !== 'undefined' ? performance.now() : Date.now()));
    this.setTimer = o.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
    this.clearTimer = o.clearTimer ?? ((id) => clearTimeout(id as ReturnType<typeof setTimeout>));
  }

  /** 새 파티 만들기 */
  create(): void { this.open('new'); }
  /** 코드로 참가 */
  join(code: string): void { this.open(code.trim().toUpperCase()); }
  get partyCode(): string { return this.room?.code ?? (this.code !== 'new' ? this.code : ''); }
  get leader(): boolean { return !!this.room && this.room.members.some((m) => m.slot === this.you && m.leader); }

  private open(code: string): void {
    this.close(false);
    this.code = code; this.wantOpen = true; this.error = ''; this.backoff = 500; this.joinFails = 0;
    this.connect();
  }

  /** 파티에서 나간다(서버에 leave를 보내고 닫는다) */
  leave(): void {
    this.send({ t: 'leave' });
    this.close(true);
  }

  /** 연결만 닫는다(leave 없이 — 페이지를 떠날 때). reset이면 방 정보도 지운다 */
  close(reset = true): void {
    this.wantOpen = false;
    if (this.retryTimer !== null) { this.clearTimer(this.retryTimer); this.retryTimer = null; }
    if (this.pingTimer !== null) { this.clearTimer(this.pingTimer); this.pingTimer = null; }
    const ws = this.ws;
    this.ws = null;
    if (ws) { ws.onopen = ws.onmessage = ws.onerror = ws.onclose = null; try { ws.close(1000, 'bye'); } catch { /* 이미 닫힘 */ } }
    this.status = 'closed';
    if (reset) { this.room = null; this.you = -1; this.inRun = false; this.mobs.clear(); this.peers.clear(); this.code = ''; }
    this.revision++;
  }

  private url(): string {
    const base = this.o.url ?? (typeof location !== 'undefined' ? (location.protocol === 'https:' ? 'wss://' : 'ws://') + location.host + PARTY_WS_PATH : PARTY_WS_PATH);
    return base + (base.includes('?') ? '&' : '?') + 'code=' + encodeURIComponent(this.code);
  }

  private connect(): void {
    this.status = this.status === 'retrying' ? 'retrying' : 'connecting';
    let ws: PartySocketLike;
    try { ws = this.o.socket ? this.o.socket(this.url()) : (new WebSocket(this.url()) as unknown as PartySocketLike); }
    catch { this.retry(); return; }
    this.ws = ws;
    ws.onopen = () => {
      this.status = 'online'; this.backoff = 500; this.revision++;
      this.sendHello();
      this.keepalive();
    };
    ws.onmessage = (ev) => { if (typeof ev.data === 'string') this.onMessage(ev.data); };
    ws.onerror = () => { /* onclose가 뒤따른다 */ };
    ws.onclose = (ev) => {
      if (this.ws !== ws) return;
      this.ws = null;
      // 서버가 거절한 이유가 있는 닫힘(교체·추방·버전·과다)은 다시 붙지 않는다
      if (ev.code === 4000 || ev.code === 4003 || ev.code === 4005 || ev.code === 4006 || ev.code === 1000) {
        this.status = 'closed'; this.wantOpen = false;
        if (!this.error) this.error = ev.code === 4000 ? 'replaced' : ev.code === 4006 ? 'kicked' : ev.code === 4003 ? 'version' : ev.code === 4005 ? 'kicked' : '';
        this.revision++;
        return;
      }
      this.retry();
    };
  }

  private retry(): void {
    if (!this.wantOpen) return;
    // 아직 코드를 받지 못한 새 파티(new)는 다시 만들지 않는다 — 처음부터 다시 누르게 한다
    if (this.code === 'new' && !this.room) { this.status = 'closed'; this.error = this.error || 'connect'; this.wantOpen = false; this.revision++; return; }
    // 아직 방에 들어간 적 없는 코드 참가(없는 코드·가득 찬 방은 업그레이드 전에 404/409로 끊긴다): 3번 실패하면 멈추고 다시 입력하게 한다(PR #243 Codex P2)
    if (!this.room && ++this.joinFails >= 3) { this.status = 'closed'; this.error = this.error || 'join-failed'; this.wantOpen = false; this.revision++; return; }
    if (this.room) this.code = this.room.code;
    this.status = 'retrying'; this.revision++;
    const wait = this.backoff * (0.8 + Math.random() * 0.4);
    this.backoff = Math.min(8000, this.backoff * 2);
    this.retryTimer = this.setTimer(() => { this.retryTimer = null; if (this.wantOpen) this.connect(); }, wait);
  }

  private keepalive(): void {
    if (this.pingTimer !== null) this.clearTimer(this.pingTimer);
    this.pingTimer = this.setTimer(() => {
      this.pingTimer = null;
      if (this.ws && this.status === 'online') { try { this.ws.send('ping'); } catch { /* 닫히는 중 */ } this.keepalive(); }
    }, PARTY_KEEPALIVE_MS);
  }

  private send(msg: PartyClientMsg): void {
    const ws = this.ws;
    if (!ws || this.status !== 'online') return;
    try { ws.send(JSON.stringify(msg)); } catch { /* 닫히는 중 */ }
  }

  /** 로비에서 영웅·레벨·이름이 바뀌었으면 hello를 다시 보낸다(서버는 로비에서만 받아 준다 — 판 보상·피해 상한이 지금 영웅 기준, PR #243 Codex P2) */
  /** 로비에 있는 동안 주기적으로(PartyLink): 영웅·레벨이 바뀌었으면 다시 알린다 — 준비를 누른 뒤 영웅을 바꾼 파티원도 서버가 지금 영웅으로 판을 연다 */
  syncProfile(): void {
    if (this.status === 'online' && this.room && this.room.phase === 'lobby') this.syncHello();
  }
  private syncHello(): void {
    const h = this.helloSent;
    if (!h || h.hero !== this.o.hero || h.lv !== this.o.lv || h.name !== this.o.name) this.sendHello();
  }
  private sendHello(): void {
    const name = this.o.name, hero = this.o.hero, lv = this.o.lv;
    this.send({ t: 'hello', v: PARTY_PROTO_VERSION, name, hero, lv });
    this.helloSent = { name, hero, lv };
  }
  ready(on: boolean): void { if (on) this.syncHello(); this.send({ t: 'ready', on }); }
  setTier(r: number): void { this.send({ t: 'tier', r }); }
  start(): void { this.syncHello(); this.send({ t: 'start' }); }
  kick(slot: number): void { this.send({ t: 'kick', slot }); }
  hit(mobId: number, dmg: number): void { if (this.inRun) this.send({ t: 'hit', m: mobId, d: Math.max(1, Math.round(dmg)) }); }
  down(): void { if (this.inRun) this.send({ t: 'down' }); }
  up(): void { if (this.inRun) this.send({ t: 'up' }); }

  /** 매 틱: 내 영웅 위치를 10Hz로(바뀐 것이 있을 때만) 보낸다 */
  update(x: number, z: number, facing: number, act: number): void {
    if (!this.inRun || this.status !== 'online') return;
    const t = this.now();
    if (t - this.lastSend < PARTY_TICK_MS) return;
    if (Math.abs(x - this.sentX) < 0.02 && Math.abs(z - this.sentZ) < 0.02 && Math.abs(facing - this.sentF) < 0.05 && act === this.sentA) return;
    this.lastSend = t; this.sentX = x; this.sentZ = z; this.sentF = facing; this.sentA = act;
    this.send({ t: 'pos', x: Math.round(x * 100) / 100, z: Math.round(z * 100) / 100, f: Math.round(facing * 100) / 100, a: act });
  }

  /** 쌓인 사건을 순서대로 넘기고 비운다 */
  drain(fn: (e: PartyEvent) => void): void {
    if (this.events.length === 0) return;
    const list = this.events.splice(0, this.events.length);
    for (const e of list) fn(e);
  }

  /** 보간: 지금 시각 − INTERP_MS 위치를 x·z·f에 채운다(몹·동료 모두) */
  sample(): void {
    const t = this.now() - INTERP_MS;
    for (const m of this.mobs.values()) this.lerp(m, t);
    for (const p of this.peers.values()) this.lerp(p, t);
  }
  private lerp(k: PartyTrack, t: number): void {
    if (k.bt <= k.at) { k.x = k.bx; k.z = k.bz; k.f = k.bf; return; }
    const r = Math.max(0, Math.min(1, (t - k.at) / (k.bt - k.at)));
    k.x = k.ax + (k.bx - k.ax) * r; k.z = k.az + (k.bz - k.az) * r; k.f = lerpAngle(k.af, k.bf, r);
  }
  private push(k: PartyTrack, x: number, z: number, f: number, t: number): void {
    if (!k.seen) { k.ax = k.bx = x; k.az = k.bz = z; k.af = k.bf = f; k.at = k.bt = t; k.x = x; k.z = z; k.f = f; k.seen = true; return; }
    // 보간 시작점 = 지금 보이는 위치(sample()을 한동안 안 불렀어도 낡은 위치에서 시작하지 않게 먼저 맞춘다)
    this.lerp(k, t - INTERP_MS);
    k.ax = k.x; k.az = k.z; k.af = k.f; k.at = t - INTERP_MS;
    k.bx = x; k.bz = z; k.bf = f; k.bt = t;
  }
  private emit(e: PartyEvent): void {
    if (this.events.length >= MAX_EVENTS) this.events.shift();
    this.events.push(e);
  }

  /** 서버 메시지 처리(시험이 직접 부른다) */
  onMessage(text: string): void {
    if (text === 'pong') return;
    let msg: PartyServerMsg;
    try { msg = JSON.parse(text) as PartyServerMsg; } catch { return; }
    const t = this.now();
    switch (msg.t) {
      case 'room':
        this.room = msg.room; this.you = msg.you; this.code = msg.room.code;
        if (msg.room.phase === 'lobby' && this.inRun && !this.lastEnd) { /* end를 기다린다 */ }
        break;
      case 'err':
        this.error = msg.code;
        break;
      case 'go':
        this.inRun = true; this.runId = msg.runId; this.tier = msg.tier; this.startAt = msg.at; this.lastEnd = null;
        this.mobs.clear(); this.peers.clear();
        this.sentX = NaN;
        this.emit({ t: 'go', runId: msg.runId, tier: msg.tier, at: msg.at });
        break;
      case 'spawn':
        for (const [id, kind, elite, x, z, hp, max] of msg.ms) {
          const k = this.mobs.get(id) ?? track(id);
          k.kind = kind; k.elite = elite === 1; k.hp = hp; k.max = max; k.st = 0; k.seen = false;
          this.push(k, x, z, 0, t);
          this.mobs.set(id, k);
          this.emit({ t: 'spawn', id, kind, elite: elite === 1, x, z, hp, max });
        }
        break;
      case 'wave':
        this.emit({ t: 'wave', n: msg.n, of: msg.of });
        break;
      case 's':
        // 스냅에 없는 동료(판 도중 나감)는 지운다 — 남으면 멈춘 유령으로 보인다(PR #243 Codex P2)
        for (const slot of this.peers.keys()) if (!msg.p.some((p) => p[0] === slot)) this.peers.delete(slot);
        for (const [slot, x, z, f, a] of msg.p) {
          if (slot === this.you) continue;
          const k = this.peers.get(slot) ?? track(slot);
          k.st = a;
          this.push(k, x, z, f, t);
          this.peers.set(slot, k);
        }
        for (const [id, x, z, hp, st, f] of msg.m) {
          const k = this.mobs.get(id);
          if (!k) continue;
          k.hp = hp; k.st = st;
          this.push(k, x, z, f, t);
        }
        break;
      case 'atk': this.emit({ t: 'atk', id: msg.id, x: msg.x, z: msg.z, r: msg.r, ms: msg.ms }); break;
      case 'hurt': this.emit({ t: 'hurt', id: msg.id, slots: msg.slots, d: msg.d }); break;
      case 'die': {
        const k = this.mobs.get(msg.id);
        if (k) { k.hp = 0; k.st = 4; }
        this.mobs.delete(msg.id);
        this.emit({ t: 'die', id: msg.id, by: msg.by });
        break;
      }
      case 'correct': this.emit({ t: 'correct', x: msg.x, z: msg.z }); break;
      case 'end':
        // 앞 판의 늦은 결과(장부 재시도 뒤 도착)는 지금 판을 끝내지 않는다(PR #243 Codex P2) — 결과만 남긴다
        if (this.inRun && msg.runId !== this.runId) { this.lastEnd = msg; break; }
        this.inRun = false; this.lastEnd = msg;
        this.mobs.clear(); this.peers.clear();
        this.emit(msg);
        break;
    }
    this.revision++;
  }
}
